import { useState, useCallback } from 'react';
import { Plus, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../common';
import Segmented from '../nucleus/Segmented';
import './CruxPanel.css';

const TABS = ['The App', 'Identity', 'Workflows'];

const PROSE_FIELDS = [
  { key: 'what', label: 'What it does' },
  { key: 'gap', label: 'The gap it fills' },
  { key: 'outcome', label: 'What users become' },
  { key: 'identity_thesis', label: 'Identity thesis' },
  { key: 'paradigm_delta', label: 'What Paradigm enables' },
  { key: 'architecture', label: 'Architecture' },
];

// ── Read-only field ──────────────────────────────────────────────────────────

function CruxField({ label, value }) {
  if (!value) return null;
  return (
    <div className="crux-field">
      <div className="crux-field__label">{label}</div>
      <div className="crux-field__value">{value}</div>
    </div>
  );
}

// ── Editable field ───────────────────────────────────────────────────────────

function CruxFieldEdit({ label, fieldKey, value, onChange }) {
  return (
    <div className="crux-field">
      <div className="crux-field__label">{label}</div>
      <textarea
        className="crux-field__textarea"
        value={value || ''}
        onChange={e => onChange(fieldKey, e.target.value)}
        rows={3}
        placeholder={`${label}…`}
      />
    </div>
  );
}

// ── Schema table ─────────────────────────────────────────────────────────────

function SchemaTable({ schemas }) {
  if (!schemas?.length) {
    return <p className="crux-empty">No schemas documented yet.</p>;
  }
  return (
    <table className="crux-schema-table">
      <thead>
        <tr>
          <th>Schema</th>
          <th>R</th>
          <th>W</th>
          <th>Stratum</th>
          <th>Purpose</th>
        </tr>
      </thead>
      <tbody>
        {schemas.map((s, i) => (
          <tr key={i}>
            <td className="crux-schema-table__uri">{s.uri}</td>
            <td className="crux-schema-table__check">{s.read ? '✓' : ''}</td>
            <td className="crux-schema-table__check">{s.write ? '✓' : ''}</td>
            <td>
              <span className={`crux-stratum crux-stratum--${s.stratum || 'default'}`}>
                {s.stratum || '—'}
              </span>
            </td>
            <td className="crux-schema-table__purpose">{s.purpose || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Workflow card (view) ─────────────────────────────────────────────────────

function WorkflowCard({ workflow }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="crux-workflow">
      <button className="crux-workflow__header" onClick={() => setOpen(o => !o)}>
        <span>{workflow.name}</span>
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>
      {open && (
        <div className="crux-workflow__steps">{workflow.steps}</div>
      )}
    </div>
  );
}

// ── Workflow card (edit) ─────────────────────────────────────────────────────

function WorkflowCardEdit({ workflow, index, onChange, onRemove }) {
  return (
    <div className="crux-workflow crux-workflow--edit">
      <div className="crux-workflow__edit-header">
        <input
          className="crux-workflow__name-input"
          value={workflow.name || ''}
          onChange={e => onChange(index, 'name', e.target.value)}
          placeholder="Workflow name…"
        />
        <button className="btn crux-workflow__remove" onClick={() => onRemove(index)} title="Remove">
          <X size={13} />
        </button>
      </div>
      <textarea
        className="crux-workflow__steps-input"
        value={workflow.steps || ''}
        onChange={e => onChange(index, 'steps', e.target.value)}
        rows={4}
        placeholder="Describe the workflow steps…"
      />
    </div>
  );
}

// ── Main CruxPanel ───────────────────────────────────────────────────────────

export default function CruxPanel({
  crux,
  appName,
  mode = 'view',       // 'view' | 'edit'
  onSave,              // (updatedCrux) => Promise<void> — only in edit mode
  saving = false,
}) {
  const [tab, setTab] = useState(0);
  const [draft, setDraft] = useState(() => crux ? { ...crux } : {});

  const handleProseChange = useCallback((key, val) => {
    setDraft(d => ({ ...d, [key]: val }));
  }, []);

  const handleWorkflowChange = useCallback((idx, field, val) => {
    setDraft(d => {
      const workflows = [...(d.workflows || [])];
      workflows[idx] = { ...workflows[idx], [field]: val };
      return { ...d, workflows };
    });
  }, []);

  const addWorkflow = useCallback(() => {
    setDraft(d => ({
      ...d,
      workflows: [...(d.workflows || []), { name: '', steps: '' }],
    }));
  }, []);

  const removeWorkflow = useCallback(idx => {
    setDraft(d => {
      const workflows = [...(d.workflows || [])];
      workflows.splice(idx, 1);
      return { ...d, workflows };
    });
  }, []);

  const isEdit = mode === 'edit';
  const data = isEdit ? draft : (crux || {});

  if (!crux && mode === 'view') {
    return (
      <div className="crux-panel crux-panel--empty">
        <p>No design overview available for this app yet.</p>
      </div>
    );
  }

  return (
    <div className="crux-panel">
      {appName && <div className="crux-panel__app-name">{appName}</div>}

      <div className="crux-tabs">
        <Segmented label="Design overview" fill value={tab} onChange={setTab}
                   options={TABS.map((t, i) => ({ id: i, label: t }))} />
      </div>

      <div className="crux-panel__body">
        {/* ── The App ── */}
        {tab === 0 && (
          <div className="crux-section">
            {isEdit
              ? PROSE_FIELDS.map(f => (
                  <CruxFieldEdit
                    key={f.key}
                    label={f.label}
                    fieldKey={f.key}
                    value={draft[f.key]}
                    onChange={handleProseChange}
                  />
                ))
              : PROSE_FIELDS.map(f => (
                  <CruxField key={f.key} label={f.label} value={data[f.key]} />
                ))
            }
          </div>
        )}

        {/* ── Identity ── */}
        {tab === 1 && (
          <div className="crux-section">
            {isEdit && (
              <p className="crux-hint">
                Schemas are managed via <code>CRUX.html</code> or <code>paradigm crux push</code>.
              </p>
            )}
            <SchemaTable schemas={data.schemas} />
          </div>
        )}

        {/* ── Workflows ── */}
        {tab === 2 && (
          <div className="crux-section">
            {!(data.workflows?.length) && !isEdit && (
              <p className="crux-empty">No workflows documented yet.</p>
            )}
            {isEdit
              ? (draft.workflows || []).map((wf, i) => (
                  <WorkflowCardEdit
                    key={i}
                    workflow={wf}
                    index={i}
                    onChange={handleWorkflowChange}
                    onRemove={removeWorkflow}
                  />
                ))
              : (data.workflows || []).map((wf, i) => (
                  <WorkflowCard key={i} workflow={wf} />
                ))
            }
            {isEdit && (
              <button className="btn crux-add-workflow" onClick={addWorkflow}>
                <Plus size={13} /> Add workflow
              </button>
            )}
          </div>
        )}
      </div>

      {isEdit && onSave && (
        <div className="crux-panel__footer">
          <Button variant="primary" size="sm" onClick={() => onSave(draft)} disabled={saving}>
            {saving ? 'Saving…' : 'Save design doc'}
          </Button>
        </div>
      )}
    </div>
  );
}
